import { Text } from "@mantine/core";
import { useState } from "react";
import { Button } from "@/components/ds/Button";
import { Modal } from "@/components/ds/Modal";

interface DialogProps {
  /**
   * Whether the dialog is open.
   */
  opened: boolean;

  /**
   * Called when the dialog is closed.
   */
  onClose: () => void;

  /**
   * The title of the dialog.
   */
  title: string;

  /**
   * The text content of the dialog.
   * If not provided, only the title will be shown.
   */
  text?: string;

  /**
   * The text for the confirm button.
   * @default "OK"
   */
  confirmLabel?: string;

  /**
   * The DS variant for the confirm button. Use "destructive" for delete/deactivate confirms.
   * @default "primary"
   */
  confirmVariant?: "primary" | "destructive";

  /**
   * The text for the cancel button.
   * If not provided, no cancel button will be shown.
   */
  cancelLabel?: string;

  /**
   * Whether the dialog is in a loading state.
   * When true, buttons are disabled and confirm button shows a spinner.
   * @default false
   */
  loading?: boolean;

  /**
   * Called when the confirm button is clicked.
   */
  onConfirm?: () => Promise<void> | void;

  /**
   * Called when the cancel button is clicked.
   */
  onCancel?: () => Promise<void> | void;

  /**
   * Whether to show a close button in the title bar.
   * @default false
   */
  withCloseButton?: boolean;

  /**
   * Additional class name for the dialog.
   */
  className?: string;
}

/**
 * A reusable confirm/alert dialog, rendered on the shared `ds/Modal` so it inherits the
 * design-system chrome. Use this for confirmations, warnings, and error messages —
 * usually via `useDialog()` rather than directly.
 */
export function Dialog({
  opened,
  onClose,
  title,
  text,
  confirmLabel = "OK",
  confirmVariant = "primary",
  cancelLabel,
  loading: externalLoading = false,
  onConfirm,
  onCancel,
  withCloseButton = false,
  className,
}: DialogProps) {
  const [internalLoading, setInternalLoading] = useState(false);

  // Combine external and internal loading states
  const isLoading = externalLoading || internalLoading;

  const handleConfirm = async () => {
    if (externalLoading) {
      // If external loading is active, just call onConfirm without managing internal state
      await onConfirm?.();
    } else {
      // Otherwise, manage internal loading state
      setInternalLoading(true);
      try {
        await onConfirm?.();
        setInternalLoading(false);
        onClose();
      } finally {
        // Ensure loading state is reset even if onConfirm throws
        setInternalLoading(false);
      }
    }
  };

  const handleCancel = async () => {
    await onCancel?.();
    onClose();
  };

  // Footer: optional secondary cancel + the primary confirm CTA (last = bottom-right).
  const buttons = [
    cancelLabel && (
      <Button
        key="cancel"
        variant="secondary"
        buttonSize="36"
        onClick={handleCancel}
        disabled={isLoading}
      >
        {cancelLabel}
      </Button>
    ),
    <Button
      key="confirm"
      variant={confirmVariant}
      buttonSize="36"
      onClick={handleConfirm}
      loading={isLoading}
      disabled={isLoading}
    >
      {confirmLabel}
    </Button>,
  ].filter(Boolean);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      withCloseButton={withCloseButton}
      className={className}
      buttons={buttons}
      mantineModalProps={{ centered: true }}
    >
      {text && <Text>{text}</Text>}
    </Modal>
  );
}
